import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Check, Loader2, CheckCircle, XCircle, AlertCircle, Save, Cloud, Server } from "lucide-react";
import { useInstallationEvents } from "../../hooks/useInstallationEvents";

// Phase 4: Simplified 4-step wizard (was 7 steps)
type WizardStep = "setup" | "plan" | "review" | "install";

interface WizardData {
  // Step 1: Setup (combined type + connection)
  installationType: "cloud" | "self-hosted" | "";
  odooUrl: string;
  odooApiKey: string;
  odooDatabase: string;
  instanceName: string;
  // Step 2: Plan (combined tier + bobs + payment)
  tier: "starter" | "pro" | "enterprise" | "";
  selectedBobs: string[];
  paymentMethod: string;
  // Step 3: Review (license generation)
  licenseKey: string;
  // Step 4: Install (verification)
  instanceId: string;
}

const STORAGE_KEY = "aiia-instance-wizard-draft";

const steps: { id: WizardStep; title: string; description: string }[] = [
  {
    id: "setup",
    title: "Setup",
    description: "Configure your Odoo connection",
  },
  {
    id: "plan",
    title: "Plan & BoBs",
    description: "Choose your plan and AI assistants",
  },
  {
    id: "review",
    title: "Review",
    description: "Confirm your configuration",
  },
  {
    id: "install",
    title: "Install",
    description: "Complete installation",
  },
];

const tiers = [
  {
    id: "starter",
    name: "Starter",
    price: "$99/mo",
    bobs: "Up to 5 BoBs",
    features: ["Basic support", "Email notifications", "Standard SLA"],
  },
  {
    id: "pro",
    name: "Professional",
    price: "$299/mo",
    bobs: "Up to 20 BoBs",
    features: ["Priority support", "Real-time alerts", "24/7 SLA"],
    recommended: true,
  },
  {
    id: "enterprise",
    name: "Enterprise",
    price: "Custom",
    bobs: "Unlimited BoBs",
    features: ["Dedicated support", "Custom integrations", "99.9% uptime SLA"],
  },
];

// Mock BoBs for selection (will be replaced with API data)
const availableBobs = [
  { id: "expense-scan", name: "Expense Scanner", category: "Finance", description: "Automatically scan and categorize expenses" },
  { id: "invoice-processor", name: "Invoice Processor", category: "Finance", description: "Process and validate invoices" },
  { id: "inventory-sync", name: "Inventory Sync", category: "Operations", description: "Keep inventory in sync across channels" },
  { id: "customer-support", name: "Customer Support", category: "Support", description: "AI-powered customer support assistant" },
  { id: "sales-forecast", name: "Sales Forecast", category: "Sales", description: "Predictive sales forecasting" },
];

export function InstanceWizard() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<WizardStep>("setup");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasDraft, setHasDraft] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [wizardData, setWizardData] = useState<WizardData>({
    installationType: "",
    odooUrl: "",
    odooApiKey: "",
    odooDatabase: "",
    instanceName: "",
    tier: "",
    selectedBobs: [],
    paymentMethod: "",
    licenseKey: "",
    instanceId: "",
  });

  const currentStepIndex = steps.findIndex((s) => s.id === currentStep);
  const progress = ((currentStepIndex + 1) / steps.length) * 100;

  // Load draft on mount
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.data && parsed.timestamp) {
          setHasDraft(true);
        }
      } catch {
        // Invalid draft, ignore
      }
    }
  }, []);

  // Auto-save every 30 seconds
  useEffect(() => {
    const hasData = wizardData.installationType || wizardData.odooUrl || wizardData.tier;
    if (!hasData) return;

    const timer = setInterval(() => {
      saveDraft();
    }, 30000);

    return () => clearInterval(timer);
  }, [wizardData]);

  const saveDraft = useCallback(() => {
    const draft = {
      data: wizardData,
      step: currentStep,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
    setLastSaved(new Date());
  }, [wizardData, currentStep]);

  const loadDraft = () => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.data) {
          setWizardData(parsed.data);
          if (parsed.step) {
            setCurrentStep(parsed.step);
          }
          setHasDraft(false);
        }
      } catch {
        // Invalid draft
      }
    }
  };

  const discardDraft = () => {
    localStorage.removeItem(STORAGE_KEY);
    setHasDraft(false);
  };

  const clearDraft = () => {
    localStorage.removeItem(STORAGE_KEY);
  };

  const canGoNext = () => {
    switch (currentStep) {
      case "setup":
        return !!(
          wizardData.installationType &&
          wizardData.odooUrl &&
          wizardData.odooApiKey &&
          wizardData.odooDatabase &&
          wizardData.instanceName
        );
      case "plan":
        return !!(wizardData.tier && wizardData.selectedBobs.length > 0);
      case "review":
        return true; // Review step just needs confirmation
      case "install":
        return !!wizardData.instanceId;
      default:
        return false;
    }
  };

  const handleNext = async () => {
    if (currentStep === "review") {
      // Transition to install step - trigger backend creation
      setIsSubmitting(true);
      try {
        // TODO: Replace with actual API call
        // const response = await api.createInstance({
        //   name: wizardData.instanceName,
        //   installation_type: wizardData.installationType,
        //   tier: wizardData.tier,
        //   odoo_url: wizardData.odooUrl,
        //   odoo_api_key: wizardData.odooApiKey,
        //   odoo_database: wizardData.odooDatabase,
        //   selected_bobs: wizardData.selectedBobs,
        // });

        // Mock instance ID for now
        const mockInstanceId = `inst-${Date.now()}`;
        updateWizardData({
          instanceId: mockInstanceId,
          licenseKey: `LIC-${mockInstanceId.toUpperCase()}`,
        });

        clearDraft();
        setCurrentStep("install");
      } catch (error) {
        console.error("Failed to create instance:", error);
      } finally {
        setIsSubmitting(false);
      }
    } else if (currentStepIndex < steps.length - 1) {
      saveDraft();
      setCurrentStep(steps[currentStepIndex + 1].id);
    } else {
      // Final step - navigate to instance detail
      clearDraft();
      navigate(`/client/instances/${wizardData.instanceId}`);
    }
  };

  const handlePrevious = () => {
    if (currentStepIndex > 0) {
      setCurrentStep(steps[currentStepIndex - 1].id);
    }
  };

  const updateWizardData = (updates: Partial<WizardData>) => {
    setWizardData((prev) => ({ ...prev, ...updates }));
  };

  const toggleBob = (bobId: string) => {
    setWizardData((prev) => {
      const selected = prev.selectedBobs.includes(bobId)
        ? prev.selectedBobs.filter((id) => id !== bobId)
        : [...prev.selectedBobs, bobId];
      return { ...prev, selectedBobs: selected };
    });
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Draft Resume Dialog */}
      {hasDraft && (
        <div className="bg-blue-900/30 border border-blue-700 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Save className="h-5 w-5 text-blue-400" />
            <div>
              <p className="text-sm font-medium text-blue-300">You have an unsaved draft</p>
              <p className="text-xs text-blue-400/70">Would you like to continue where you left off?</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={discardDraft}
              className="px-3 py-1.5 text-sm text-slate-400 hover:text-slate-300 transition-colors"
            >
              Discard
            </button>
            <button
              onClick={loadDraft}
              className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              Resume Draft
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div>
        <button
          onClick={() => navigate("/client/dashboard")}
          className="flex items-center gap-2 text-sm text-slate-400 hover:text-slate-300 transition-colors mb-4"
        >
          <ChevronLeft className="h-4 w-4" />
          Back to Dashboard
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-100">
              Create New Instance
            </h1>
            <p className="mt-1 text-sm text-slate-400">
              Set up your Odoo instance with AI assistants in 4 easy steps
            </p>
          </div>
          {lastSaved && (
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Save className="h-3 w-3" />
              Saved {lastSaved.toLocaleTimeString()}
            </div>
          )}
        </div>
      </div>

      {/* Progress Bar - Simplified 4 steps */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-slate-700 p-6">
        <div className="flex items-center justify-between mb-4">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <div
                className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-colors ${
                  index < currentStepIndex
                    ? "bg-emerald-500 border-emerald-500"
                    : index === currentStepIndex
                      ? "bg-blue-500 border-blue-500"
                      : "bg-slate-700 border-slate-600"
                }`}
              >
                {index < currentStepIndex ? (
                  <Check className="h-5 w-5 text-white" />
                ) : (
                  <span className="text-sm font-semibold text-white">
                    {index + 1}
                  </span>
                )}
              </div>
              {index < steps.length - 1 && (
                <div
                  className={`w-24 h-0.5 mx-2 transition-colors ${
                    index < currentStepIndex ? "bg-emerald-500" : "bg-slate-600"
                  }`}
                />
              )}
            </div>
          ))}
        </div>
        <div className="w-full bg-slate-700 rounded-full h-2">
          <div
            className="bg-blue-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="mt-4 text-center">
          <h3 className="text-lg font-semibold text-slate-100">
            Step {currentStepIndex + 1}: {steps[currentStepIndex].title}
          </h3>
          <p className="text-sm text-slate-400">
            {steps[currentStepIndex].description}
          </p>
        </div>
      </div>

      {/* Step Content */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-slate-700 p-8">
        {/* Step 1: Setup (combined type + connection) */}
        {currentStep === "setup" && (
          <div className="space-y-8">
            {/* Instance Name */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Instance Name
              </label>
              <input
                type="text"
                value={wizardData.instanceName}
                onChange={(e) => updateWizardData({ instanceName: e.target.value })}
                placeholder="My Production Instance"
                className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:border-blue-500"
              />
              <p className="mt-1 text-xs text-slate-500">
                A friendly name to identify this instance
              </p>
            </div>

            {/* Installation Type */}
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-4">
                Deployment Type
              </h3>
              <div className="grid gap-4 md:grid-cols-2">
                <button
                  onClick={() => updateWizardData({ installationType: "cloud" })}
                  className={`p-5 rounded-lg border-2 transition-all text-left ${
                    wizardData.installationType === "cloud"
                      ? "border-blue-500 bg-blue-500/10"
                      : "border-slate-600 hover:border-slate-500"
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <Cloud className="h-6 w-6 text-blue-400" />
                    <h4 className="text-lg font-semibold text-slate-100">Cloud Hosted</h4>
                  </div>
                  <p className="text-sm text-slate-400">
                    We manage everything. Quick setup, automatic updates.
                  </p>
                  <div className="mt-3 text-xs text-emerald-400 font-medium">
                    Recommended for most users
                  </div>
                </button>
                <button
                  onClick={() => updateWizardData({ installationType: "self-hosted" })}
                  className={`p-5 rounded-lg border-2 transition-all text-left ${
                    wizardData.installationType === "self-hosted"
                      ? "border-blue-500 bg-blue-500/10"
                      : "border-slate-600 hover:border-slate-500"
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <Server className="h-6 w-6 text-purple-400" />
                    <h4 className="text-lg font-semibold text-slate-100">Self-Hosted</h4>
                  </div>
                  <p className="text-sm text-slate-400">
                    Deploy on your infrastructure. Full control.
                  </p>
                  <div className="mt-3 text-xs text-slate-500">
                    Requires technical expertise
                  </div>
                </button>
              </div>
            </div>

            {/* Odoo Connection */}
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-4">
                Odoo Connection
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Odoo URL
                  </label>
                  <input
                    type="url"
                    value={wizardData.odooUrl}
                    onChange={(e) => updateWizardData({ odooUrl: e.target.value })}
                    placeholder="https://your-company.odoo.com"
                    className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Database Name
                    </label>
                    <input
                      type="text"
                      value={wizardData.odooDatabase}
                      onChange={(e) => updateWizardData({ odooDatabase: e.target.value })}
                      placeholder="your_database"
                      className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      API Key
                    </label>
                    <input
                      type="password"
                      value={wizardData.odooApiKey}
                      onChange={(e) => updateWizardData({ odooApiKey: e.target.value })}
                      placeholder="Your Odoo API key"
                      className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:border-blue-500"
                    />
                  </div>
                </div>
                <p className="text-xs text-slate-500">
                  Find your API key in Odoo: Settings → Users → Your User → Security tab
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Plan & BoBs (combined tier + bobs) */}
        {currentStep === "plan" && (
          <div className="space-y-8">
            {/* Tier Selection */}
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-4">
                Select Your Plan
              </h3>
              <div className="grid gap-4 md:grid-cols-3">
                {tiers.map((tier) => (
                  <button
                    key={tier.id}
                    onClick={() => updateWizardData({ tier: tier.id as WizardData["tier"] })}
                    className={`p-5 rounded-lg border-2 transition-all text-left relative ${
                      wizardData.tier === tier.id
                        ? "border-blue-500 bg-blue-500/10"
                        : "border-slate-600 hover:border-slate-500"
                    }`}
                  >
                    {tier.recommended && (
                      <div className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-emerald-500 text-white text-xs font-medium rounded">
                        Recommended
                      </div>
                    )}
                    <h4 className="text-lg font-semibold text-slate-100">{tier.name}</h4>
                    <div className="text-2xl font-bold text-blue-400 my-2">{tier.price}</div>
                    <div className="text-sm text-emerald-400 mb-3">{tier.bobs}</div>
                    <ul className="space-y-1.5">
                      {tier.features.map((feature, idx) => (
                        <li key={idx} className="text-xs text-slate-400 flex items-center gap-2">
                          <Check className="h-3 w-3 text-emerald-400" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </button>
                ))}
              </div>
            </div>

            {/* BoB Selection */}
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-2">
                Select AI Assistants (BoBs)
              </h3>
              <p className="text-sm text-slate-400 mb-4">
                Choose the BoBs you want to install. You can add more later.
              </p>
              <div className="grid gap-3 md:grid-cols-2">
                {availableBobs.map((bob) => (
                  <button
                    key={bob.id}
                    onClick={() => toggleBob(bob.id)}
                    className={`p-4 rounded-lg border-2 transition-all text-left flex items-start gap-3 ${
                      wizardData.selectedBobs.includes(bob.id)
                        ? "border-blue-500 bg-blue-500/10"
                        : "border-slate-600 hover:border-slate-500"
                    }`}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      wizardData.selectedBobs.includes(bob.id)
                        ? "border-blue-500 bg-blue-500"
                        : "border-slate-500"
                    }`}>
                      {wizardData.selectedBobs.includes(bob.id) && (
                        <Check className="h-3 w-3 text-white" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-100">{bob.name}</span>
                        <span className="text-xs px-1.5 py-0.5 bg-slate-700 text-slate-400 rounded">
                          {bob.category}
                        </span>
                      </div>
                      <p className="text-xs text-slate-400 mt-1">{bob.description}</p>
                    </div>
                  </button>
                ))}
              </div>
              {wizardData.selectedBobs.length > 0 && (
                <p className="mt-3 text-sm text-slate-400">
                  {wizardData.selectedBobs.length} BoB{wizardData.selectedBobs.length !== 1 ? "s" : ""} selected
                </p>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Review */}
        {currentStep === "review" && (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-slate-100 mb-6">
              Review Your Configuration
            </h3>

            {/* Summary Cards */}
            <div className="grid gap-4 md:grid-cols-2">
              {/* Instance Details */}
              <div className="bg-slate-700/50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-slate-400 mb-3">Instance Details</h4>
                <dl className="space-y-2">
                  <div className="flex justify-between">
                    <dt className="text-sm text-slate-400">Name</dt>
                    <dd className="text-sm font-medium text-slate-100">{wizardData.instanceName}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm text-slate-400">Type</dt>
                    <dd className="text-sm font-medium text-slate-100 capitalize">{wizardData.installationType}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm text-slate-400">Odoo URL</dt>
                    <dd className="text-sm font-medium text-slate-100 truncate max-w-[200px]">{wizardData.odooUrl}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm text-slate-400">Database</dt>
                    <dd className="text-sm font-medium text-slate-100">{wizardData.odooDatabase}</dd>
                  </div>
                </dl>
              </div>

              {/* Plan Details */}
              <div className="bg-slate-700/50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-slate-400 mb-3">Plan Details</h4>
                <dl className="space-y-2">
                  <div className="flex justify-between">
                    <dt className="text-sm text-slate-400">Plan</dt>
                    <dd className="text-sm font-medium text-slate-100 capitalize">{wizardData.tier}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm text-slate-400">Price</dt>
                    <dd className="text-sm font-medium text-blue-400">
                      {tiers.find(t => t.id === wizardData.tier)?.price}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm text-slate-400">BoBs Selected</dt>
                    <dd className="text-sm font-medium text-slate-100">{wizardData.selectedBobs.length}</dd>
                  </div>
                </dl>
              </div>
            </div>

            {/* Selected BoBs List */}
            <div className="bg-slate-700/50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-slate-400 mb-3">Selected AI Assistants</h4>
              <div className="flex flex-wrap gap-2">
                {wizardData.selectedBobs.map((bobId) => {
                  const bob = availableBobs.find(b => b.id === bobId);
                  return bob ? (
                    <span
                      key={bobId}
                      className="px-3 py-1.5 bg-blue-500/20 text-blue-300 text-sm rounded-full"
                    >
                      {bob.name}
                    </span>
                  ) : null;
                })}
              </div>
            </div>

            {/* Confirmation Notice */}
            <div className="bg-emerald-900/20 border border-emerald-700/50 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-emerald-300 font-medium">Ready to create your instance</p>
                  <p className="text-xs text-emerald-400/70 mt-1">
                    Click "Create Instance" to begin the installation process. This will connect to your Odoo instance and install the selected BoBs.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Install (real-time verification) */}
        {currentStep === "install" && (
          <VerifyInstallationStep
            instanceId={wizardData.instanceId}
            navigate={navigate}
          />
        )}
      </div>

      {/* Navigation - Stacks on mobile, row on desktop */}
      <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-3">
        <button
          onClick={handlePrevious}
          disabled={currentStepIndex === 0 || currentStep === "install"}
          className="flex items-center justify-center gap-2 px-6 py-3 rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors w-full sm:w-auto"
        >
          <ChevronLeft className="h-5 w-5" />
          Previous
        </button>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          {currentStep !== "install" && (
            <button
              onClick={saveDraft}
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-slate-600 text-slate-400 hover:text-slate-300 hover:bg-slate-700 transition-colors w-full sm:w-auto"
            >
              <Save className="h-4 w-4" />
              Save Draft
            </button>
          )}

          <button
            onClick={handleNext}
            disabled={!canGoNext() || isSubmitting}
            className="flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors w-full sm:w-auto"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Creating...
              </>
            ) : currentStep === "review" ? (
              <>
                Create Instance
                <Check className="h-5 w-5" />
              </>
            ) : currentStep === "install" ? (
              <>
                Go to Dashboard
                <ChevronRight className="h-5 w-5" />
              </>
            ) : (
              <>
                Next
                <ChevronRight className="h-5 w-5" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Real-time Installation Progress Component
 * Displays live installation progress with SSE updates.
 * Auto-redirects to dashboard on completion.
 */
interface VerifyInstallationStepProps {
  instanceId: string;
  navigate: ReturnType<typeof useNavigate>;
}

function VerifyInstallationStep({ instanceId, navigate }: VerifyInstallationStepProps) {
  const { progress, status, message, error, isConnected, isComplete, reconnect } = useInstallationEvents(instanceId);

  // Auto-redirect to dashboard on completion (after 3 seconds)
  useEffect(() => {
    if (isComplete) {
      const timer = setTimeout(() => {
        navigate(`/client/instances/${instanceId}`);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [isComplete, instanceId, navigate]);

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-2xl font-semibold text-slate-100 mb-2">
          Installing Your Instance
        </h3>
        <p className="text-slate-400">
          Real-time progress updates from your installation
        </p>
      </div>

      {/* Installation Successful */}
      {isComplete && (
        <div className="bg-green-900/20 border border-green-700 rounded-lg p-6 text-center">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h4 className="text-xl font-semibold text-green-400 mb-2">
            Installation Successful!
          </h4>
          <p className="text-slate-300 mb-4">
            Your AIIA Connector has been successfully installed and activated.
          </p>
          <p className="text-sm text-slate-400">
            Redirecting to your instance in 3 seconds...
          </p>
        </div>
      )}

      {/* Error message with troubleshooting link */}
      {error && !isComplete && (
        <div className="bg-red-900/20 border border-red-700 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <XCircle className="h-8 w-8 text-red-500 flex-shrink-0 mt-1" />
            <div className="flex-1">
              <h4 className="text-lg font-semibold text-red-400 mb-2">
                Installation Failed
              </h4>
              <p className="text-slate-300 mb-4">{error}</p>
              <div className="flex gap-3">
                <button
                  onClick={reconnect}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-white transition-colors"
                >
                  Retry Connection
                </button>
                <a
                  href="/docs/troubleshooting/installation"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white transition-colors inline-flex items-center gap-2"
                >
                  <AlertCircle className="h-4 w-4" />
                  Troubleshooting Guide
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Progress bar */}
      {!isComplete && !error && (
        <div className="space-y-3">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-slate-400">Progress</span>
            <span className="text-slate-300 font-semibold">{progress}%</span>
          </div>
          <div className="w-full bg-slate-700 rounded-full h-4 overflow-hidden">
            <div
              className="bg-gradient-to-r from-blue-500 to-purple-500 h-full transition-all duration-500 ease-out flex items-center justify-end pr-2"
              style={{ width: `${progress}%` }}
            >
              {progress > 10 && (
                <span className="text-xs font-semibold text-white">
                  {progress}%
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Step-by-step messages */}
      {!isComplete && !error && (
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-6">
          <div className="flex items-start gap-4">
            {status === 'active' ? (
              <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0 mt-1" />
            ) : status === 'failed' ? (
              <XCircle className="h-6 w-6 text-red-500 flex-shrink-0 mt-1" />
            ) : (
              <Loader2 className="h-6 w-6 text-blue-500 animate-spin flex-shrink-0 mt-1" />
            )}
            <div className="flex-1">
              <h4 className="text-lg font-semibold text-slate-200 mb-2">
                {getStatusLabel(status)}
              </h4>
              <p className="text-slate-400">{message || 'Waiting for installation to start...'}</p>
            </div>
          </div>
        </div>
      )}

      {/* Connection Status */}
      <div className="flex items-center justify-center gap-2 text-sm">
        <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`} />
        <span className="text-slate-400">
          {isConnected ? 'Connected to installation stream' : 'Disconnected - attempting to reconnect...'}
        </span>
      </div>

      {/* Installation Steps Timeline */}
      {!isComplete && !error && (
        <div className="bg-slate-800/30 border border-slate-700 rounded-lg p-6">
          <h5 className="text-sm font-semibold text-slate-300 mb-4">Installation Steps</h5>
          <div className="space-y-3">
            <InstallationStep
              label="Connecting to Odoo instance"
              completed={progress >= 20}
              active={progress >= 0 && progress < 20}
            />
            <InstallationStep
              label="Installing AIIA Connector"
              completed={progress >= 50}
              active={progress >= 20 && progress < 50}
            />
            <InstallationStep
              label="Configuring selected BoBs"
              completed={progress >= 80}
              active={progress >= 50 && progress < 80}
            />
            <InstallationStep
              label="Verifying installation"
              completed={progress === 100}
              active={progress >= 80 && progress < 100}
            />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Helper component for installation step timeline
 */
interface InstallationStepProps {
  label: string;
  completed: boolean;
  active: boolean;
}

function InstallationStep({ label, completed, active }: InstallationStepProps) {
  return (
    <div className="flex items-center gap-3">
      {completed ? (
        <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
      ) : active ? (
        <Loader2 className="h-5 w-5 text-blue-500 animate-spin flex-shrink-0" />
      ) : (
        <div className="h-5 w-5 rounded-full border-2 border-slate-600 flex-shrink-0" />
      )}
      <span className={`text-sm ${completed ? 'text-slate-300' : active ? 'text-blue-400' : 'text-slate-500'}`}>
        {label}
      </span>
    </div>
  );
}

/**
 * Helper function to convert status codes to human-readable labels
 */
function getStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    'pending': 'Waiting to start...',
    'installing': 'Installing connector',
    'verifying': 'Verifying installation',
    'active': 'Installation complete',
    'failed': 'Installation failed',
  };

  return labels[status] || status;
}
